import { settingsPath } from '../services/platform.js';
import { alertController } from '@ionic/core/components';
import { html } from '../lib/html.js';
import { toastError } from '../lib/ui.js';
import { tabbarFooter } from '../lib/tabs.js';
import { storage } from '../services/storage.js';
import { location } from '../services/location.js';
import { fmtDistance, fmtRating, showcase } from '../services/showcase.js';
import { Page, go } from './base.js';
import { verifiedTag } from './showcase-ui.js';
import { CATEGORY, CATEGORY_KEYS, categoryOf, vehicleId, vehicles } from '../services/vehicles.js';
import { catIcon } from './vehicle-hero.js';

const UF = /^(AC|AL|AP|AM|BA|CE|DF|ES|GO|MA|MT|MS|MG|PA|PB|PR|PE|PI|RJ|RN|RS|RO|RR|SC|SP|SE|TO)$/;
/** Guarda só o MODO de busca e a cidade digitada; a posição do GPS nunca é guardada. */
const MODE_KEY = 'tc.showcase.where';

/**
 * Buscar (botão redondo do espaço Pessoal): empresas da vitrine, mais perto primeiro, com nota e linhas atendidas.
 *   Onde: "Perto de você" (localização aproximada, pedida só aqui e depois de explicar) ou uma cidade digitada.
 *   Filtros: Linha (ou "Para o <veículo da garagem>") · Verificadas · Nota 4+ · Aberta agora · Serviço; ordem: Mais perto / Melhor avaliadas.
 *   A busca por nome/serviço filtra a lista já carregada.
 */
class ShowcasePage extends Page {
  template() {
    return html`
      <ion-content class="ion-padding tc-page root-page has-tabbar" fullscreen>
        <ion-refresher slot="fixed"><ion-refresher-content></ion-refresher-content></ion-refresher>
        <div class="narrow">
          <div class="top-row where"></div>
          <h1 class="page-title">Buscar</h1>
          <label class="searchbar">
            <ion-icon name="search-outline" aria-hidden="true"></ion-icon>
            <input type="search" class="q" placeholder="Empresa, serviço ou bairro" aria-label="Buscar empresa" enterkeyhint="search" />
          </label>
          <div class="chips chip-row" role="group" aria-label="Filtros"></div>
          <div class="results"></div>
        </div>
      </ion-content>
      ${tabbarFooter('showcase')}`;
  }

  setup() {
    this.filters = { line: null, lineLabel: '', verified: false, minRating: false, openNow: false, specialty: null, sort: 'distance' };
    this.$('.q').addEventListener('input', () => this.renderResults());
    this.$('ion-content').addEventListener('click', (e) => {
      const el = e.target.closest('[data-action]');
      if (el) return this.action(el.dataset.action);
      const f = e.target.closest('[data-filter]');
      if (f) return this.toggle(f.dataset.filter);
      const row = e.target.closest('[data-go]');
      if (row) go(row.dataset.go);
    });
    this.$('ion-refresher').addEventListener('ionRefresh', async (e) => {
      await this.load();
      e.target.complete();
    });
  }

  async onEnter() {
    this.$('tc-tabbar')?.refresh();
    if (!this.specialties) showcase.specialties().then((r) => (this.specialties = r.items)).catch(() => {});
    this.where = storage.get(MODE_KEY); // { mode: 'gps' } | { mode: 'city', city, state }
    if (!this.where) {
      // primeira vez: explica antes do pedido do Android
      this.renderWhere();
      this.renderChips();
      this.$('.results').innerHTML = askPanel().value;
      return;
    }
    await this.load();
  }

  async load() {
    this.renderWhere();
    this.renderChips();
    if (!this.where) return;
    this.$('.results').innerHTML = '<div class="center-small"><ion-spinner></ion-spinner></div>';
    try {
      let q;
      if (this.where.mode === 'gps') {
        q = await location.approximate();
      } else {
        q = { city: this.where.city, state: this.where.state };
      }
      const f = this.filters;
      const r = await showcase.search({ ...q, verified: f.verified, minRating: f.minRating ? 4 : undefined, openNow: f.openNow, specialty: f.specialty ?? undefined, line: f.line ?? undefined, sort: f.sort, limit: 50 });
      this.items = r.items;
      this.error = null;
    } catch (err) {
      this.items = null;
      this.error = err;
      if (err.code?.startsWith('LOCATION_')) {
        this.$('.results').innerHTML = locationError(err).value;
        return;
      }
    }
    this.renderResults();
  }

  renderWhere() {
    const w = this.where;
    const text = !w ? 'Escolha onde procurar' : w.mode === 'gps' ? 'Perto de você' : `Em ${w.city}/${w.state}`;
    this.$('.where').innerHTML = html`
      <button type="button" class="space-btn where-btn" data-action="${w ? 'change' : 'use-gps'}" aria-label="Onde procurar: ${text}. Alterar">
        <ion-icon name="location-outline" class="where-ic" aria-hidden="true"></ion-icon>
        <span>${text}</span>
        <ion-icon class="chev" name="chevron-down" aria-hidden="true"></ion-icon>
      </button>`.value;
  }

  renderChips() {
    const f = this.filters;
    const spec = f.specialty && this.specialties?.find((s) => s.key === f.specialty)?.name;
    const chip = (key, label, on, icon = '') => html`<button type="button" class="chip ${on ? 'on' : ''}" data-filter="${key}" aria-pressed="${on ? 'true' : 'false'}">${icon ? html`<ion-icon name="${icon}" aria-hidden="true"></ion-icon>` : ''}${label}</button>`;
    const L = f.line && CATEGORY[f.line];
    this.$('.chips').innerHTML = html`
      ${chip('line', f.lineLabel || 'Linha', Boolean(f.line), L ? L.icon : 'tc-truck')}
      ${chip('verified', 'Verificadas', f.verified, 'shield-checkmark-outline')}
      ${chip('minRating', '4+', f.minRating, 'star-outline')}
      ${chip('openNow', 'Aberta agora', f.openNow, 'time-outline')}
      ${chip('specialty', spec ?? 'Serviço', Boolean(f.specialty), 'construct-outline')}`.value;
  }

  renderResults() {
    const box = this.$('.results');
    if (!this.items) {
      if (this.error) box.innerHTML = html`<div class="panel"><p class="panel-text">${this.error.message}</p><div class="panel-foot"><ion-button expand="block" fill="outline" data-action="reload">Tentar de novo</ion-button></div></div>`.value;
      return;
    }
    const term = this.$('.q').value.trim().toLocaleLowerCase('pt-BR');
    const list = term
      ? this.items.filter((w) => [w.name, w.district, ...w.specialties.map((s) => s.name)].some((x) => x?.toLocaleLowerCase('pt-BR').includes(term)))
      : this.items;
    const f = this.filters;
    const filtered = f.verified || f.minRating || f.openNow || f.specialty || f.line || term;
    const context = this.where.mode === 'gps' ? 'em até 50 km' : `em ${this.where.city}`;
    box.innerHTML = html`
      <div class="result-bar">
        <span><b>${list.length} ${list.length === 1 ? 'empresa' : 'empresas'}</b>${f.verified ? (list.length === 1 ? ' verificada' : ' verificadas') : ''}${f.line ? ` de ${CATEGORY[f.line].label.toLowerCase()}` : ''} ${context}</span>
        <button type="button" class="sort-btn" data-action="sort">${f.sort === 'rating' ? 'Melhor avaliadas' : 'Mais perto'}<ion-icon name="chevron-down" aria-hidden="true"></ion-icon></button>
      </div>
      ${list.length
        ? html`<div class="panel">${list.map(row)}</div>`
        : html`
          <div class="panel empty-panel">
            <div class="empty-badge"><ion-icon name="storefront-outline"></ion-icon></div>
            <strong>${filtered ? 'Nada com esse filtro' : 'Nenhuma empresa por aqui ainda'}</strong>
            <p>${filtered ? 'Tire algum filtro ou busque outro termo.' : 'O TicketCar está chegando na sua região. Conhece uma boa empresa? Indique o app para ela.'}</p>
          </div>`}`.value;
  }

  async toggle(key) {
    if (key === 'specialty') return this.pickSpecialty();
    if (key === 'line') return this.pickLine();
    this.filters[key] = !this.filters[key];
    await this.load();
  }

  /**
   * Linha: escolher um veículo da garagem ("Para o Scania R450") já filtra pelas empresas que atendem a linha dele;
   * também dá para escolher a linha direto.
   */
  async pickLine() {
    if (!this.garage) this.garage = await vehicles.list().then((r) => r.items.filter((x) => x.access)).catch(() => []);
    const opts = [];
    for (const it of this.garage) {
      const c = categoryOf(it.vehicle);
      const name = it.access?.nickname || [it.vehicle.brand, it.vehicle.model].filter(Boolean).join(' ') || vehicleId(it.vehicle);
      opts.push({ value: `v:${c.key}:${name}`, label: `Para ${name.length > 22 ? `${name.slice(0, 21)}…` : name} · ${c.line}` });
    }
    for (const k of CATEGORY_KEYS) opts.push({ value: `l:${k}`, label: CATEGORY[k].label });
    const cur = this.filters.line ? (this.filters.lineLabel.startsWith('Para ') ? `v:${this.filters.line}:${this.filters.lineLabel.slice(5)}` : `l:${this.filters.line}`) : '';
    const a = await alertController.create({
      header: 'Para qual veículo?',
      message: 'Mostramos só as empresas que atendem a linha dele.',
      inputs: [{ type: 'radio', label: 'Todas as linhas', value: '', checked: !cur }, ...opts.map((o) => ({ type: 'radio', label: o.label, value: o.value, checked: o.value === cur }))],
      buttons: [{ text: 'Cancelar', role: 'cancel' }, { text: 'Aplicar', role: 'confirm' }],
    });
    await a.present();
    const { role, data } = await a.onDidDismiss();
    if (role !== 'confirm') return;
    const v = String(data?.values ?? '');
    if (!v) Object.assign(this.filters, { line: null, lineLabel: '' });
    else if (v.startsWith('v:')) {
      const [, line, ...name] = v.split(':');
      Object.assign(this.filters, { line, lineLabel: `Para ${name.join(':')}` });
    } else Object.assign(this.filters, { line: v.slice(2), lineLabel: CATEGORY[v.slice(2)].label });
    await this.load();
  }

  async pickSpecialty() {
    const list = this.specialties ?? (await showcase.specialties().then((r) => (this.specialties = r.items)).catch(() => []));
    const a = await alertController.create({
      header: 'Tipo de serviço',
      inputs: [{ type: 'radio', label: 'Todos', value: '', checked: !this.filters.specialty }, ...list.map((s) => ({ type: 'radio', label: s.name, value: s.key, checked: this.filters.specialty === s.key }))],
      buttons: [{ text: 'Cancelar', role: 'cancel' }, { text: 'Aplicar', role: 'confirm' }],
    });
    await a.present();
    const { role, data } = await a.onDidDismiss();
    if (role !== 'confirm') return;
    this.filters.specialty = data?.values || null;
    await this.load();
  }

  async action(name) {
    if (name === 'reload') return this.load();
    if (name === 'use-gps') return this.useGps();
    if (name === 'use-city') return this.askCity();
    if (name === 'change') {
      const a = await alertController.create({
        header: 'Onde procurar?',
        buttons: [
          { text: 'Perto de mim', handler: () => void this.useGps() },
          { text: 'Escolher uma cidade', handler: () => void this.askCity() },
          { text: 'Cancelar', role: 'cancel' },
        ],
      });
      return a.present();
    }
    if (name === 'sort') {
      const a = await alertController.create({
        header: 'Ordenar por',
        inputs: [
          { type: 'radio', label: 'Mais perto', value: 'distance', checked: this.filters.sort !== 'rating' },
          { type: 'radio', label: 'Melhor avaliadas', value: 'rating', checked: this.filters.sort === 'rating' },
        ],
        buttons: [{ text: 'Cancelar', role: 'cancel' }, { text: 'Aplicar', role: 'confirm' }],
      });
      await a.present();
      const { role, data } = await a.onDidDismiss();
      if (role !== 'confirm') return;
      this.filters.sort = data?.values === 'rating' ? 'rating' : 'distance';
      return this.load();
    }
  }

  async useGps() {
    this.where = { mode: 'gps' };
    storage.set(MODE_KEY, this.where);
    await this.load();
  }

  async askCity() {
    const a = await alertController.create({
      header: 'Buscar por cidade',
      inputs: [
        { name: 'city', placeholder: 'Cidade', value: this.where?.city ?? '', attributes: { maxlength: 80, autocapitalize: 'words' } },
        { name: 'state', placeholder: 'UF (ex.: SP)', value: this.where?.state ?? '', attributes: { maxlength: 2, autocapitalize: 'characters' } },
      ],
      buttons: [{ text: 'Cancelar', role: 'cancel' }, { text: 'Buscar', role: 'confirm' }],
    });
    await a.present();
    const { role, data } = await a.onDidDismiss();
    if (role !== 'confirm') return;
    const city = String(data?.values?.city ?? '').trim();
    const state = String(data?.values?.state ?? '').trim().toUpperCase();
    if (city.length < 2 || !UF.test(state)) return toastError('Informe a cidade e a UF (ex.: Franca e SP).');
    this.where = { mode: 'city', city, state };
    storage.set(MODE_KEY, this.where);
    await this.load();
  }
}

/** Linha da lista. */
function row(w) {
  const meta = [fmtDistance(w.distanceKm), w.district].filter(Boolean).join(' · ');
  return html`
    <button type="button" class="shop-row tappable" data-go="/oficinas/${w.id}">
      <span class="shop-thumb">${w.cover ? html`<img src="${w.cover}" alt="" loading="lazy" />` : html`<ion-icon name="storefront-outline"></ion-icon>`}</span>
      <span class="row-main">
        <span class="row-title">${w.name}</span>
        <span class="shop-meta">
          ${w.rating ? html`<ion-icon class="star" name="star"></ion-icon><b>${fmtRating(w.rating.avg)}</b><span>(${w.rating.count})</span>` : html`<span class="new-tag">Nova no TicketCar</span>`}
          ${meta ? html`<span>· ${meta}</span>` : ''}
        </span>
        ${(w.lines ?? ['LIGHT']).length ? html`<span class="cat-icons shop-lines" aria-label="Linhas: ${(w.lines ?? ['LIGHT']).map((k) => CATEGORY[k]?.line).join(', ')}">${(w.lines ?? ['LIGHT']).map((k) => catIcon(k, 'sm'))}</span>` : ''}
        ${w.verified || w.specialties.length
          ? html`<span class="tags">${w.verified ? verifiedTag() : ''}${w.specialties.slice(0, w.verified ? 1 : 2).map((s) => html`<span class="tag">${s.name}</span>`)}</span>`
          : ''}
        ${w.open ? html`<span class="open-line ${w.open.open ? 'open' : ''}">${w.open.open ? 'Aberta' : 'Fechada'} · ${w.open.label.charAt(0).toLowerCase() + w.open.label.slice(1)}</span>` : ''}
      </span>
    </button>`;
}

/** Primeira vez: por que pedimos a localização (antes do pedido do Android). */
const askPanel = () => html`
  <div class="panel empty-panel">
    <div class="empty-badge"><ion-icon name="location-outline"></ion-icon></div>
    <strong>Empresas perto de você</strong>
    <p>Para ordenar pela distância, o TicketCar usa sua localização aproximada (cerca de 1 km), só agora. Ela não é guardada.</p>
    <ion-button expand="block" class="empty-cta" data-action="use-gps"><ion-icon slot="start" name="locate-outline"></ion-icon>Usar minha localização</ion-button>
    <ion-button expand="block" fill="clear" data-action="use-city">Prefiro escolher a cidade</ion-button>
  </div>`;

const locationError = (err) => html`
  <div class="panel empty-panel">
    <div class="empty-badge"><ion-icon name="location-outline"></ion-icon></div>
    <strong>${err.code === 'LOCATION_DENIED' ? 'Sem acesso à localização' : 'Localização indisponível'}</strong>
    <p>${err.code === 'LOCATION_DENIED' ? `Você pode liberar em ${settingsPath('location')}, ou buscar pela cidade.` : err.message}</p>
    <ion-button expand="block" class="empty-cta" data-action="use-city">Buscar por cidade</ion-button>
    <ion-button expand="block" fill="clear" data-action="reload">Tentar de novo</ion-button>
  </div>`;

customElements.define('page-showcase', ShowcasePage);
